import React from 'react';
import { titleize } from 'inflection';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import FollowButton, {
  FollowableType
} from 'buyside/components/presentation/FollowButton/followButton';
import FundProfileCardIntroRequest from 'buyside/components/presentation/FundProfileCard/FundProfileCardIntroRequest/fundProfileCardIntroRequest';
import FundProfileIndexItemHighlights from 'buyside/components/presentation/FundProfileIndexPage/FundProfileIndexItem/FundProfileIndexItemHighlights/fundProfileIndexItemHighlights';
import Link from 'buyside/components/presentation/Link/link';
import ProfileImage from 'buyside/components/presentation/ProfileImage/profileImage';

import { employerProfileUrl, employerProfileVanityUrl } from 'buyside/util/urlHelper';

import { FundProfileIndexItemFragment } from 'interfaces/graphql-typings';

import styles from './fundProfileIndexItemStyles.scss';

const FundProfileIndexItem: React.FC<{
  node: FundProfileIndexItemFragment;
}> = ({ node }) => {
  const {
    aumRange,
    focus,
    foundingYear,
    fundType,
    highlights,
    id: fundId,
    lastReturn,
    logoProfileUrl,
    name: fundName,
    pendingIntroRequest,
    sharpeRatio,
    strategy,
    vanityLink
  } = node;

  const { ltm, ytd, sinceInception } = lastReturn || {};

  const dataPoints = [
    {
      name: 'LTM • YTD',
      val: `${ltm ? `${ltm}%` : 'N/A'} • ${ytd ? `${ytd}%` : 'N/A'}`
    },
    {
      name: `Since ${foundingYear}`,
      val: sinceInception ? `${sinceInception}%` : null
    },
    { name: 'Focus', val: focus ? titleize(focus) : null },
    { name: 'Sharpe', val: sharpeRatio },
    { name: 'Aum Range', val: aumRange }
  ];

  return (
    <div className={styles.fundProfileIndexItem}>
      <div className={styles.itemHeader}>
        <div className={styles.headerInfo}>
          {logoProfileUrl && (
            <ProfileImage source={logoProfileUrl} className={styles.profileImage} />
          )}
          <div>
            <Link
              to={vanityLink ? employerProfileVanityUrl(vanityLink) : employerProfileUrl(fundId)}
              className={styles.fundName}
            >
              <h4>{fundName}</h4>
            </Link>
            <div>
              {fundType} • {strategy}
            </div>
          </div>
        </div>
        <div className={styles.headerActions}>
          <FollowButton
            buttonDisplay="Fund"
            buttonType={BUTTON_TYPES.LINK}
            followedClassName={styles.followedButton}
            id={fundId}
            size={BUTTON_SIZES.SMALL}
            type={FollowableType.FUND}
          />
          <Button
            size={BUTTON_SIZES.SMALL}
            text="View Details"
            type={BUTTON_TYPES.LINK}
            url={vanityLink ? employerProfileVanityUrl(vanityLink) : '/'}
          />
        </div>
      </div>
      <br />
      <div className={styles.dataPoints}>
        {dataPoints.map(dataPoint => (
          <div key={dataPoint.name}>
            <div className={styles.dataPointName}>{dataPoint.name}</div>
            <div className={styles.dataPointValue}>{dataPoint.val || 'N/A'}</div>
          </div>
        ))}
      </div>
      {highlights && (
        <>
          <br />
          <FundProfileIndexItemHighlights highlights={highlights} />
        </>
      )}
      {pendingIntroRequest && (
        <>
          <br />
          <FundProfileCardIntroRequest
            fundName={fundName || ''}
            pendingIntroRequest={pendingIntroRequest}
          />
        </>
      )}
    </div>
  );
};

export default FundProfileIndexItem;
